/**
 * meshViewModels — pure derivations from `CommunalTopology` +
 * `StandingLedger` into the shapes the mesh sidebar renders. Kept
 * separate from the components on purpose (per the M5 brief: "keep the
 * view-models pure and testable") — every function here takes plain data
 * in, returns plain data out, no React/DOM/mesh access, so
 * test/meshViewModels.test.ts can assert the degrade-not-deny framing
 * and the coverage-gap CTA copy without spinning up a mesh at all.
 */
import type { CommunalGap, CommunalTopology, MeshRosterEntry, StandingLedger, StandingSnapshot } from '@unstable-legion/core';

// ── Capacity meter ──────────────────────────────────────────────────────
//
// Two DELIBERATELY SEPARATE meters, per product feedback that a bare
// "N seats free" reads as ambiguous (a plea for more hosts? spare
// slots?) when folded into the same number as assembly progress:
//
//   1. COVERAGE — "is the model assembled at all / does it need hosts."
//      `coveragePercent`/`ready`/`gaps`/`gapMessage`. Meaningful (and
//      shown) until `ready` — this IS the "needs more hosts" signal.
//   2. OCCUPANCY — "now that it's up, how much room-wide chat headroom
//      is there." `occupancy`, only populated once `ready`. NEVER a
//      call for more hosts — it's whole-room spare CONSUMER capacity
//      (concurrent chat sessions the assembled pipeline can currently
//      admit), expressed as an occupancy fraction ("12 of 15 chat slots
//      open"), never a bare free count that could be misread as a
//      headcount request.

export interface OccupancyView {
  /** Concurrent chat sessions currently occupying a lane, at the
   * room-wide bottleneck segment (see `deriveOccupancy`'s doc comment). */
  active: number;
  /** Total concurrent-session capacity at that same bottleneck segment. */
  total: number;
  /** `total - active`, clamped >= 0 — matches `topology.seats` exactly
   * when a bottleneck segment exists (cross-checked in
   * test/meshViewModels.test.ts). */
  free: number;
  /** True when `free === 0` — the soft-queue case. Never "full/blocked". */
  atCapacity: boolean;
  /** Human copy: an occupancy fraction ("12 of 15 chat slots open · 3
   * active now") when there's headroom, or the soft-queue framing
   * ("Room busy — new chats queue by contribution, you won't be turned
   * away.") at `atCapacity`. Whole-room, never per-person. */
  label: string;
}

/** Room-wide spare CONSUMER capacity, from the SAME bottleneck segment
 * `buildCommunalTopology`'s own `seats` field is the min over (headroom
 * summed per segment) — recomputed here (not just reading `.seats`)
 * because the occupancy label needs the bottleneck segment's total/active
 * breakdown too, not just its free count. `undefined` when there's no
 * communal layer space at all (`topology.segments` empty — the whole
 * model fits in the driver's own local layers, so there's no room-wide
 * bottleneck to report). */
export function deriveOccupancy(topology: CommunalTopology): OccupancyView | undefined {
  if (topology.gaps.length > 0 || topology.segments.length === 0) return undefined;

  let best: { active: number; total: number; free: number } | undefined;
  for (const segment of topology.segments) {
    const total = segment.candidates.reduce((sum, c) => sum + c.maxSessions, 0);
    const active = segment.candidates.reduce((sum, c) => sum + c.activeSessions, 0);
    const free = segment.candidates.reduce((sum, c) => sum + Math.max(0, c.maxSessions - c.activeSessions), 0);
    if (!best || free < best.free) best = { active, total, free };
  }
  if (!best) return undefined;

  const atCapacity = best.free <= 0;
  const label = atCapacity
    ? 'Room busy — new chats queue by contribution, you won’t be turned away.'
    : `${best.free} of ${best.total} chat slot${best.total === 1 ? '' : 's'} open · ${best.active} active now`;

  return { ...best, atCapacity, label };
}

export interface CapacityView {
  /** 0-100, rounded — `topology.coverageFraction * 100`. */
  coveragePercent: number;
  /** True iff the mesh can currently route an end-to-end chat (no gaps). */
  ready: boolean;
  gaps: readonly CommunalGap[];
  /** "Qwen3-8B · Q4_K_M" — passed straight through from
   * `chatModelSource.ts`'s `ChatModelConfig.modelLabel`, so every string
   * this view-model produces below names the model, not a bare number. */
  modelLabel: string;
  /** Prominent, model-named one-liner for the COVERAGE meter —
   * "Assembling Qwen3-8B · Q4_K_M — 60% ready" while incomplete,
   * "Qwen3-8B · Q4_K_M ready" once assembled. Deliberately says nothing
   * about seats/occupancy — that's `occupancy`'s own, visually separate
   * meter. Product requirement: the mesh's model identity is never
   * buried behind a bare percentage. */
  statusLine: string;
  /** Plain-language CTA for the not-ready case — the brief's exact
   * framing, model-named ("Qwen3-8B · Q4_K_M 60% assembled — layers
   * 14–27 need a host."). Empty when `ready` is true. */
  gapMessage: string;
  /** Room-wide chat headroom, only once `ready` — see module doc comment. */
  occupancy?: OccupancyView;
}

export function deriveCapacityView(topology: CommunalTopology, modelLabel: string): CapacityView {
  const coveragePercent = Math.round(topology.coverageFraction * 100);
  const ready = topology.gaps.length === 0;

  const statusLine = ready ? `${modelLabel} ready` : `Assembling ${modelLabel} — ${coveragePercent}% ready`;

  const gapMessage = ready
    ? ''
    : `${modelLabel} ${coveragePercent}% assembled — layers ${topology.gaps
        .map((g) => `${g.layerStart}–${g.layerEnd}`)
        .join(', ')} need a host. Contribute your GPU to unlock chat.`;

  return {
    coveragePercent,
    ready,
    gaps: topology.gaps,
    modelLabel,
    statusLine,
    gapMessage,
    occupancy: deriveOccupancy(topology),
  };
}

// ── Driver-side chat notice ──────────────────────────────────────────────
//
// The chat pane must be HONEST about failures — never a silent hang. A
// driver whose route can't be found, whose host failed, or that's
// mid-reconnect gets a clear, actionable message instead of a frozen
// spinner. Pure + testable (test/meshViewModels.test.ts): plain status in,
// plain notice out.

export interface ChatNoticeView {
  kind: 'error' | 'retrying';
  message: string;
}

/** Minimal shape of `useCommunalChat`'s status this derivation needs —
 * avoids importing the full union just for a phase/error/reason read. */
export interface ChatStatusLike {
  phase: string;
  error?: string;
  reason?: string;
}

/** True when a raw reason/error string looks like a lost-host / connection
 * failure rather than a coverage gap or a generic error. */
function looksLikeHostLoss(text: string): boolean {
  return /\b(host|peer|connection|disconnect|left|lost|timed? ?out|timeout|stop)\b/i.test(text);
}

/**
 * Derive the chat pane's failure/reconnect notice. Returns `undefined`
 * when there's nothing to say (idle/planning/running-clean/finished).
 *
 *   - error + "no feasible route"/coverage gap → the actionable "layers
 *     X–Y need a host" framing (pulled from the live capacity gaps).
 *   - error + host-loss → "Lost connection to a host …".
 *   - aborted (involuntary) → the reason, framed as a stop, not a hang.
 *   - running after ≥1 replan → "Reconnecting to a host…" (transient).
 */
export function deriveChatNotice(
  status: ChatStatusLike,
  restartCount: number,
  capacity: CapacityView,
  /** True when THIS device can't run a model stage locally (see
   * `isThinDriverForModel`). A thin device hosts nothing, so it can only chat
   * when other members cover EVERY layer including the first stage — the
   * generic "assembling…" copy is misleading for it, so we say so plainly. */
  isThinDevice = false,
): ChatNoticeView | undefined {
  if (status.phase === 'error') {
    const err = status.error ?? 'unknown error';
    if (/no feasible|coverage|assembl|need a host|gap/i.test(err)) {
      const gaps = capacity.gaps.map((g) => `${g.layerStart}–${g.layerEnd}`).join(', ');
      if (isThinDevice) {
        // Don't pretend it's merely "assembling": a device too small to run a
        // stage FUNDAMENTALLY depends on other members hosting the whole
        // pipeline (first stage included). Tell the user that + the way out.
        return {
          kind: 'error',
          message: gaps
            ? `This device isn't powerful enough to run a model stage itself, so it needs other members to host the whole pipeline — layers ${gaps} still need a host. It'll work once capable members cover them; otherwise join from a more powerful device.`
            : `This device isn't powerful enough to run a model stage itself, so it can only chat when other members host the model — and no member is hosting the first stage right now. Join from a more powerful device, or wait for a capable member to contribute.`,
        };
      }
      return {
        kind: 'error',
        message: gaps
          ? `${capacity.modelLabel} isn't fully assembled yet — layers ${gaps} need a host. Contribute your GPU or wait for another member.`
          : `${capacity.modelLabel} isn't fully assembled yet — waiting for hosts to cover every layer.`,
      };
    }
    if (looksLikeHostLoss(err)) {
      return { kind: 'error', message: `Lost connection to a host and couldn't recover: ${err}. Try again.` };
    }
    return { kind: 'error', message: `Something went wrong: ${err}` };
  }

  if (status.phase === 'aborted') {
    const reason = status.reason ?? 'stopped';
    if (/\buser\b/i.test(reason)) return undefined; // an explicit user stop isn't an error notice
    return { kind: 'error', message: `The chat stopped: ${reason}. Try sending again.` };
  }

  if (status.phase === 'running' && restartCount > 0) {
    return { kind: 'retrying', message: 'Lost a host mid-reply — reconnecting to another…' };
  }

  return undefined;
}

// ── Topology map ─────────────────────────────────────────────────────────

export interface TopologySegmentView {
  layerStart: number;
  layerEnd: number;
  kind: 'local' | 'covered' | 'gap';
  /** Display label for a covered/local segment — nick if known, else a
   * shortened peerId. Undefined for gaps. */
  label?: string;
  peerId?: string;
  isSelf: boolean;
  /** How many distinct hosts cover this exact layer-range. A covered
   * segment can be served by MANY redundant hosts; the bar names the
   * primary in `label` and this count reveals the rest ("+2 more"). 1 for a
   * lone host, undefined for local/gap. This is what fixes the old
   * "only the first host is ever shown" blind spot. */
  hostCount?: number;
}

/** Full [0, totalLayers) picture for the layer-bar visual: the driver's
 * own local range, every covered communal segment (winning candidate),
 * and every gap — in layer order. */
export function deriveTopologySegments(
  topology: CommunalTopology,
  opts: { selfId: string; nickOf: (peerId: string) => string | undefined },
): TopologySegmentView[] {
  const segments: TopologySegmentView[] = [];
  if (topology.driverLayers > 0) {
    segments.push({
      layerStart: 0,
      layerEnd: topology.driverLayers,
      kind: 'local',
      label: 'you',
      peerId: opts.selfId,
      isSelf: true,
    });
  }

  type Ordered = { layerStart: number; layerEnd: number; kind: 'covered' | 'gap'; peerId?: string; hostCount?: number };
  const ordered: Ordered[] = [
    ...topology.segments.map((s) => ({
      layerStart: s.layerStart,
      layerEnd: s.layerEnd,
      kind: 'covered' as const,
      peerId: s.candidates[0]?.peerId,
      // Distinct hosts covering this exact range — a segment's candidates
      // can include the same peer twice across churn transients, so dedupe.
      hostCount: new Set(s.candidates.map((c) => c.peerId)).size,
    })),
    ...topology.gaps.map((g) => ({ layerStart: g.layerStart, layerEnd: g.layerEnd, kind: 'gap' as const })),
  ].sort((a, b) => a.layerStart - b.layerStart);

  for (const seg of ordered) {
    const isSelf = seg.peerId === opts.selfId;
    segments.push({
      layerStart: seg.layerStart,
      layerEnd: seg.layerEnd,
      kind: seg.kind,
      peerId: seg.peerId,
      isSelf,
      ...(seg.kind === 'covered' ? { hostCount: seg.hostCount } : {}),
      label:
        seg.kind === 'gap'
          ? undefined
          : isSelf
            ? 'you'
            : (seg.peerId && opts.nickOf(seg.peerId)) || (seg.peerId ? shortPeerId(seg.peerId) : undefined),
    });
  }

  return segments;
}

// ── Crew scale line ──────────────────────────────────────────────────────
//
// The topology bar names ONE host per layer-range, which hid the real
// answer to "how many people are hosting this?" — a segment can be covered
// by many redundant hosts, and the mesh can hold several complete copies at
// once. This aggregate answers that at a glance, scoped to YOUR CREW (the
// peers you've discovered covering this model — the same roster the
// topology is built from). A global census across peers you haven't
// discovered is a separate, coordinator-scale concern (see the census
// backlog item), deliberately not conflated here.

export interface CrewScaleView {
  /** Distinct host peers across the assembled pipeline's segments — the
   * crew you can see, EXCLUDING you (you're rendered as "you" in the bar). */
  hosts: number;
  /** Redundant COMPLETE copies your crew can run independently: the min,
   * over every covered segment, of how many distinct hosts cover it. 0 when
   * the model isn't fully assembled (a gap exists). Distinct from `seats`:
   * this counts physical model copies loaded across the crew, `seats` counts
   * concurrent chats. Undercounts hosts on an ALTERNATIVE layer-tiling than
   * the one the greedy topology chose — a known limit of a single-tiling
   * view, not the census. */
  fullModels: number;
  /** Total concurrent chat capacity — min over segments of Σ maxSessions
   * (the bottleneck). 0 when not assembled. Mirrors `occupancy.total`. */
  seats: number;
  /** Compact, model-agnostic summary: "3 hosts · 2 full copies · 15 seats".
   * Omits any count that's zero/one where it would read as noise. */
  label: string;
}

/** Aggregate "your crew" scale from the discovered topology. `undefined`
 * when there are no communal segments at all (nothing to summarize — the
 * whole model is in the driver's own local layers). */
export function deriveCrewScale(topology: CommunalTopology): CrewScaleView | undefined {
  if (topology.segments.length === 0) return undefined;

  // Count ALL distinct hosts covering the model, INCLUDING you when you're
  // one of them — a solo self-host must read "1 host", not "0". (A pure
  // driver that hosts no communal segment simply isn't a candidate, so it
  // isn't counted.) This MUST include self to stay consistent with
  // fullModels/seats below, which already do.
  const distinctHosts = new Set<string>();
  for (const seg of topology.segments) {
    for (const c of seg.candidates) distinctHosts.add(c.peerId);
  }
  const hosts = distinctHosts.size;

  const assembled = topology.gaps.length === 0;
  const fullModels = assembled
    ? topology.segments.reduce((min, s) => Math.min(min, new Set(s.candidates.map((c) => c.peerId)).size), Number.POSITIVE_INFINITY)
    : 0;
  const seats = assembled
    ? topology.segments.reduce((min, s) => Math.min(min, s.candidates.reduce((sum, c) => sum + c.maxSessions, 0)), Number.POSITIVE_INFINITY)
    : 0;

  const parts: string[] = [`${hosts} host${hosts === 1 ? '' : 's'}`];
  if (fullModels > 1) parts.push(`${fullModels} full copies`);
  if (seats > 0) parts.push(`${seats} seat${seats === 1 ? '' : 's'}`);

  return {
    hosts,
    fullModels: Number.isFinite(fullModels) ? fullModels : 0,
    seats: Number.isFinite(seats) ? seats : 0,
    label: parts.join(' · '),
  };
}

export function shortPeerId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

export function nickLookup(roster: readonly MeshRosterEntry[]): (peerId: string) => string | undefined {
  const byId = new Map(roster.map((r) => [r.peerId, r.nick] as const));
  return (peerId) => byId.get(peerId);
}

// ── Standing / "your contribution" panel ─────────────────────────────────

export type StandingTier = 'top' | 'contributing' | 'newcomer' | 'debt';

export interface StandingView {
  standing: number;
  priorityScore: number;
  tier: StandingTier;
  /** Plain-language, degrade-not-deny framing — NEVER "blocked"/"denied". */
  message: string;
  /** This peer's own hosted layer range, when it's contributing compute
   * right now (from `useCommunalHost`'s `claim`). Undefined = not hosting. */
  hostedRange?: { layerStart: number; layerEnd: number };
}

/** `topStandings` should be `ledger.topContributors(n, now)` for a
 * reasonably large `n` (large enough to cover the whole visible mesh) —
 * passed in rather than recomputed here so the leaderboard and the
 * standing panel always agree on the same ranking snapshot. */
export function deriveStandingView(
  ledger: StandingLedger,
  selfId: string,
  now: number,
  topStandings: readonly StandingSnapshot[],
  hostedRange?: { layerStart: number; layerEnd: number },
): StandingView {
  const mine = ledger.myStanding(selfId, now);
  const hasHistory = ledger.hasHistory(selfId);

  let tier: StandingTier;
  let message: string;
  if (!hasHistory) {
    tier = 'newcomer';
    message = "You're new here — everyone gets fair access from the start. Contribute your GPU to build priority.";
  } else if (mine.standing <= 0) {
    tier = 'debt';
    message = 'Contribute your GPU to move up the queue.';
  } else {
    const rank = topStandings.findIndex((s) => s.peerId === selfId);
    const topBand = Math.max(1, Math.ceil(topStandings.length * 0.2));
    if (rank !== -1 && rank < topBand) {
      tier = 'top';
      message = "You're a top contributor — priority access.";
    } else {
      tier = 'contributing';
      message = "You're contributing — priority access is building.";
    }
  }

  return { standing: mine.standing, priorityScore: mine.priorityScore, tier, message, hostedRange };
}

// ── Leaderboard ────────────────────────────────────────────────────────

export interface LeaderboardEntry {
  rank: number;
  peerId: string;
  label: string;
  standing: number;
  isSelf: boolean;
}

export function deriveLeaderboard(
  topStandings: readonly StandingSnapshot[],
  opts: { selfId: string; nickOf: (peerId: string) => string | undefined },
): LeaderboardEntry[] {
  return topStandings.map((s, i) => ({
    rank: i + 1,
    peerId: s.peerId,
    label: opts.nickOf(s.peerId) ?? shortPeerId(s.peerId),
    standing: s.standing,
    isSelf: s.peerId === opts.selfId,
  }));
}
